/**
 * "Last Updated" byline.
 *
 * The date runs three days behind today, as it does on the original. Liquid
 * already renders it server side, so crawlers and no-JS visitors get a real
 * date and nothing reflows on load. This refresh exists because Shopify can
 * serve the page from its edge cache, which would otherwise freeze the date at
 * whatever day the cached copy was rendered on, and because a tab left open
 * overnight and restored from bfcache would show yesterday's.
 *
 * One difference from the FunnelOS original, which computes the date on the
 * server only: past the first paint this reads the visitor's clock rather than
 * the shop's timezone. For a reader far enough east that is the date they would
 * call today, which is what a "last updated" line should say.
 *
 * Wrapped in an IIFE: top-level declarations in a classic script share one
 * global lexical scope with every other script on the page.
 */
(() => {
  const DAYS_BEHIND = 3;

  const refreshArticleDate = () => {
    const el = document.querySelector('[data-article-date]');
    if (!el) return;

    const date = new Date();
    date.setDate(date.getDate() - DAYS_BEHIND);
    const formatted = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    if (el.textContent.trim() !== formatted) el.textContent = formatted;
  };

  refreshArticleDate();
  window.addEventListener('pageshow', refreshArticleDate);
})();

/**
 * nf-adv02 results timeline.
 *
 * Ported from the React component at
 * apps/app/src/funnels/nasalfresh-md/nf-adv02/_timeline.tsx in
 * michaeltoddbeauty/FunnelOS.
 *
 * The fill and the milestone states derive from ONE number measured against the
 * RAIL (not the section, not each card's centre), which is what keeps the
 * fill's leading edge and the dot it lights up in sync.
 */
class NfAdv02Timeline extends HTMLElement {
  /** @type {HTMLElement | null} */ #rail = null;
  /** @type {HTMLElement | null} */ #fill = null;
  /** @type {HTMLElement[]} */ #items = [];
  /** @type {HTMLElement[]} */ #dots = [];
  /** @type {number[]} */ #fractions = [];
  #railTop = 0;
  #railHeight = 1;
  #frame = 0;
  /** @type {ResizeObserver | null} */ #observer = null;

  connectedCallback() {
    this.#rail = this.querySelector('[data-rail]');
    this.#fill = this.querySelector('[data-fill]');
    this.#items = Array.from(this.querySelectorAll('[data-milestone]'));
    this.#dots = Array.from(this.querySelectorAll('[data-dot]'));

    if (!this.#rail || !this.#fill || this.#items.length === 0) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#reveal();
      return;
    }

    this.#measure();
    this.#update();

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize);

    // Guarded so engines without ResizeObserver do not throw and kill the rest.
    if (typeof ResizeObserver !== 'undefined') {
      this.#observer = new ResizeObserver(() => this.#onResize());
      this.#observer.observe(this.#rail);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    this.#observer?.disconnect();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  /** Light every milestone, for users who asked for no motion. */
  #reveal() {
    this.#fill.style.transform = 'scaleY(1)';
    for (const item of this.#items) item.style.opacity = '1';
  }

  #measure() {
    const box = this.#rail.getBoundingClientRect();
    this.#railTop = box.top + window.scrollY;
    this.#railHeight = Math.max(1, box.height);
    this.#fractions = this.#dots.map((dot) => {
      const b = dot.getBoundingClientRect();
      return (b.top + window.scrollY + b.height / 2 - this.#railTop) / this.#railHeight;
    });
  }

  #update() {
    const travelled = window.scrollY + window.innerHeight * 0.6 - this.#railTop;
    const progress = Math.min(1, Math.max(0, travelled / this.#railHeight));
    this.#fill.style.transform = `scaleY(${progress})`;
    this.#items.forEach((item, i) => {
      item.style.opacity = progress >= (this.#fractions[i] ?? 1) ? '1' : '0.4';
    });
  }

  /* Arrow fields so the listeners keep their binding and can be removed. */

  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #onResize = () => {
    this.#measure();
    this.#update();
  };
}

if (!customElements.get('nf-adv02-timeline')) {
  customElements.define('nf-adv02-timeline', NfAdv02Timeline);
}
